'use client';

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { resolveVariant, resolveClientName, type InstanceConfig } from './instance-config';
import { procurementConfig } from './variants/procurement';
import { fundingConfig } from './variants/funding';

const VARIANT_MAP: Record<string, InstanceConfig> = {
  procurement: procurementConfig,
  funding: fundingConfig,
};

const InstanceConfigContext = createContext<InstanceConfig | null>(null);

/**
 * @param variant    Resolved by the SERVER from the live container env and passed down.
 * @param clientName Same. Both are optional so any other mount point still works.
 *
 * Why props: this is a `'use client'` component, so it cannot read the container env. It
 * used to rely on Next.js inlining `NEXT_PUBLIC_*` into the client bundle at build time,
 * which pinned one built image to one client and made the ladder impossible. The root
 * layout is now `force-dynamic`, resolves both values server-side, and passes them here.
 * When they are absent the runtime bridge is the fallback — on the client that reads the
 * window global the server injected. See config/runtime-config.ts.
 */
export function InstanceConfigProvider({
  children,
  variant: variantProp,
  clientName: clientNameProp,
}: {
  children: ReactNode;
  variant?: string;
  clientName?: string;
}) {
  const config = useMemo(() => {
    const variant = variantProp ?? resolveVariant();
    const base = VARIANT_MAP[variant] ?? procurementConfig;
    return { ...base, clientName: clientNameProp ?? resolveClientName() };
  }, [variantProp, clientNameProp]);

  return (
    <InstanceConfigContext.Provider value={config}>
      {children}
    </InstanceConfigContext.Provider>
  );
}

export function useInstanceConfig(): InstanceConfig {
  const ctx = useContext(InstanceConfigContext);
  if (!ctx) {
    throw new Error('useInstanceConfig must be used within an InstanceConfigProvider');
  }
  return ctx;
}
